import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, Paper } from '@mui/material';

interface NodeData {
  label: string;
  nodeType: string;
  parameters?: any;
  description?: string;
}

const IF_NODE_TYPE = 'core.if';
const SWITCH_NODE_TYPE = 'core.switch';

export const BaseNode: React.FC<NodeProps<NodeData>> = ({ data, selected }) => {
  const isIfNode = data.nodeType === IF_NODE_TYPE;
  const isSwitchNode = data.nodeType === SWITCH_NODE_TYPE;
  const switchCases: Array<{ value: string }> = data.parameters?.cases || [];

  const getNodeColor = () => {
    if (data.nodeType?.startsWith('trigger')) return '#ff6b6b';
    if (data.nodeType?.startsWith('action')) return '#4ecdc4';
    if (data.nodeType?.startsWith('core')) return '#45b7d1';
    return '#95a5a6';
  };

  return (
    <Paper
      elevation={selected ? 8 : 2}
      sx={{
        minWidth: 180,
        maxWidth: 280,
        maxHeight: 220,
        border: `2px solid ${selected ? getNodeColor() : 'transparent'}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          bgcolor: getNodeColor(),
          color: 'white',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" noWrap>
          {data.label}
        </Typography>
      </Box>
      {data.description && (
        <Box
          className="nowheel nodrag"
          onWheelCapture={(e) => e.stopPropagation()}
          sx={{
            px: 2,
            py: 1,
            maxHeight: 120,
            overflowY: 'auto',
            overflowX: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {data.description}
          </Typography>
        </Box>
      )}

      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />

      {isIfNode ? (
        <>
          <Handle
            id="true"
            type="source"
            position={Position.Bottom}
            style={{ left: '30%', background: '#4caf50', width: 12, height: 12 }}
            title="True branch"
          />
          <Handle
            id="false"
            type="source"
            position={Position.Bottom}
            style={{ left: '70%', background: '#f44336', width: 12, height: 12 }}
            title="False branch"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: '20%', pb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.6rem', fontWeight: 'bold' }}>
              True
            </Typography>
            <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.6rem', fontWeight: 'bold' }}>
              False
            </Typography>
          </Box>
        </>
      ) : isSwitchNode ? (
        <>
          {switchCases.map((c, idx) => {
            const total = switchCases.length + 1;
            const leftPct = ((idx + 1) / (total + 1)) * 100;
            const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63'];
            const color = colors[idx % colors.length];
            return (
              <React.Fragment key={`case-${idx}`}>
                <Handle
                  id={`case-${idx}`}
                  type="source"
                  position={Position.Bottom}
                  style={{ left: `${leftPct}%`, background: color, width: 10, height: 10 }}
                  title={`Case ${idx}: ${c.value}`}
                />
              </React.Fragment>
            );
          })}
          <Handle
            id="default"
            type="source"
            position={Position.Bottom}
            style={{ left: `${((switchCases.length + 1) / (switchCases.length + 2)) * 100}%`, background: '#757575', width: 10, height: 10 }}
            title="Default"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', px: 1, pb: 0.5 }}>
            {switchCases.map((c, idx) => {
              const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63'];
              return (
                <Typography key={`lbl-${idx}`} variant="caption" sx={{ color: colors[idx % colors.length], fontSize: '0.55rem', fontWeight: 'bold' }}>
                  {c.value || `${idx}`}
                </Typography>
              );
            })}
            <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.55rem', fontWeight: 'bold' }}>
              Def
            </Typography>
          </Box>
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
      )}
    </Paper>
  );
};

















